import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

export function escapeHtml(text) {
  if (text == null || text === '') return '';
  const d = document.createElement('div');
  d.textContent = String(text);
  return d.innerHTML;
}

/** Shared styles for print window and PDF (matches structured report HTML). */
export const REPORT_DOCUMENT_CSS = `
  body{margin:0;padding:24px;font-family:ui-sans-serif,system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif;color:#111827;line-height:1.45;background:#fff;}
  h1{font-size:22px;margin:0 0 8px;font-weight:700;}
  .muted{color:#6b7280;font-size:12px;margin-bottom:16px;}
  table{width:100%;border-collapse:collapse;font-size:12px;margin-top:8px;}
  th,td{border:1px solid #e5e7eb;padding:10px 8px;text-align:left;vertical-align:top;}
  th{background:#f9fafb;font-weight:600;}
  .grid{display:grid;grid-template-columns:repeat(2,1fr);gap:14px 20px;margin-bottom:20px;}
  .grid > div{border:1px solid #e5e7eb;border-radius:8px;padding:12px;background:#fafafa;}
  .grid .muted{font-size:11px;text-transform:uppercase;letter-spacing:0.03em;margin-bottom:4px;}
  h2{font-size:16px;margin:20px 0 10px;font-weight:600;}
  @media print{
    body{padding:12px;}
    @page{margin:12mm;}
  }
`;

/**
 * Opens a new window with HTML and triggers the print dialog.
 * @returns {{ ok: true } | { ok: false, reason: 'popup_blocked' }}
 */
export function openPrintWindow(title, bodyHtml) {
  const w = window.open('', '_blank');
  if (!w) {
    return { ok: false, reason: 'popup_blocked' };
  }
  const safeTitle = escapeHtml(title);
  const doc = `<!DOCTYPE html><html><head><meta charset="utf-8"/><title>${safeTitle}</title>
<style>${REPORT_DOCUMENT_CSS}</style></head><body>${bodyHtml}
<script>window.addEventListener('load',function(){setTimeout(function(){window.print();},200);});</script>
</body></html>`;
  w.document.open();
  w.document.write(doc);
  w.document.close();
  return { ok: true };
}

/**
 * Build a multi-page A4 PDF from a canvas (image).
 */
function saveCanvasToPdf(canvas, filename) {
  const imgData = canvas.toDataURL('image/png');
  const pdf = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 36;
  const contentWidth = pageWidth - margin * 2;
  const contentHeight = pageHeight - margin * 2;
  const imgWidth = contentWidth;
  const imgHeight = (canvas.height * imgWidth) / canvas.width;

  let heightLeft = imgHeight;
  let y = margin;

  while (heightLeft > 0) {
    pdf.addImage(imgData, 'PNG', margin, y, imgWidth, imgHeight);
    heightLeft -= contentHeight;
    if (heightLeft > 0) {
      pdf.addPage();
      y -= contentHeight;
    }
  }

  const safeName = (filename || 'report').replace(/[^\w-]+/g, '_');
  pdf.save(`${safeName}.pdf`);
}

/**
 * Rasterizes a DOM node to a multi-page A4 PDF (portrait, pt units).
 */
export async function saveDomAsPdf(domNode, filename) {
  if (!domNode) return { ok: false, reason: 'no_node' };

  const canvas = await html2canvas(domNode, {
    scale: 2,
    useCORS: true,
    logging: false,
    backgroundColor: '#ffffff',
    windowWidth: domNode.scrollWidth,
    windowHeight: domNode.scrollHeight,
  });

  saveCanvasToPdf(canvas, filename);
  return { ok: true };
}

/**
 * Renders the same structured HTML as print (with REPORT_DOCUMENT_CSS) in a hidden iframe,
 * then rasterizes to PDF. Use this when the on-screen React tree (tables, SVG icons) does not
 * capture well with html2canvas.
 */
export async function saveHtmlAsPdf(bodyHtml, filename) {
  const iframe = document.createElement('iframe');
  iframe.setAttribute('title', 'pdf-export');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.style.cssText =
    'position:fixed;left:-9999px;top:0;width:780px;min-height:400px;border:0;opacity:0;pointer-events:none;';

  document.body.appendChild(iframe);

  const idoc = iframe.contentDocument;
  if (!idoc) {
    document.body.removeChild(iframe);
    throw new Error('Could not create export frame');
  }

  const safeName = escapeHtml((filename || 'report').replace(/\.pdf$/i, ''));

  idoc.open();
  idoc.write(
    `<!DOCTYPE html><html><head><meta charset="utf-8"/><title>${safeName}</title><style>${REPORT_DOCUMENT_CSS}</style></head><body>${bodyHtml}</body></html>`
  );
  idoc.close();

  if (idoc.fonts?.ready) {
    try {
      await idoc.fonts.ready;
    } catch {
      /* ignore */
    }
  }
  await new Promise((r) => setTimeout(r, 80));

  const body = idoc.body;
  const canvas = await html2canvas(body, {
    scale: 2,
    useCORS: true,
    logging: false,
    backgroundColor: '#ffffff',
    width: body.scrollWidth,
    height: body.scrollHeight,
    windowWidth: body.scrollWidth,
    windowHeight: body.scrollHeight,
    onclone: (clonedDoc) => {
      const b = clonedDoc.body;
      if (b) {
        b.style.overflow = 'visible';
        b.style.height = 'auto';
      }
    },
  });

  document.body.removeChild(iframe);
  saveCanvasToPdf(canvas, filename);
  return { ok: true };
}
